import { Link } from 'react-router-dom';
import LegalShell from '../../components/legal/LegalShell';
import { CONSENT_VERSIONS } from '../../auth/useConsent';

export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy" version={CONSENT_VERSIONS.privacy} effective="2026-08-06">
      <p>
        Andrew Paris LLC, d/b/a MediaShare-Link ("Company," "we," "us,"
        "our") operates the
        OculusMind.AI website and the OculusMind.AI AI-agent platform
        (collectively, the "Services"). This Privacy Policy explains how
        we collect, use, and share information when you use our Services.
      </p>

      <h2>Information We Collect</h2>
      <p>When you use the Services, we may collect the following categories of information:</p>
      <ul>
        <li>Email address (provided during account registration).</li>
        <li>Authentication credentials, multi-factor authentication state, and session information.</li>
        <li>Chat messages, prompts, instructions, and other text you send to our AI agents.</li>
        <li>Documents, images, audio, video, and other files you upload for an agent to process.</li>
        <li>Search queries, research instructions, and follow-up clarifications you direct an agent to act on.</li>
        <li>Agent configuration, saved preferences, workflows, and project state.</li>
        <li>Account-level usage information, including timestamps, feature usage, and agent activity history.</li>
        <li>
          Device and connection metadata, including IP address, browser
          User-Agent, approximate geolocation derived from IP, advertising
          identifiers, cookies, and similar information transmitted by your
          browser or device.
        </li>
      </ul>

      <h2>How We Use Your Information</h2>

      <h3>1. Operating the Services</h3>
      <p>
        We use the information we collect to authenticate your account, route
        your prompts to the AI agents you have selected, store your conversation
        and project history, deliver requested features, and otherwise operate
        the Services.
      </p>

      <h3>2. Operational Support and Service Improvement</h3>
      <p>
        Our operational support personnel may access your data — including chat
        interactions, agent task history, and uploaded files — to diagnose
        issues, investigate suspected abuse, improve reliability, and enhance
        the Services.
      </p>

      <h3>3. Advertising and Marketing</h3>
      <p>
        Portions of the Services — including guest and free-tier access — may
        display advertisements. Where we do, we may share device metadata,
        advertising identifiers, and usage information with advertising
        partners, advertising networks, and measurement providers in order to
        deliver advertising — including interest-based advertising selected for
        you based on that information — measure campaign performance, and
        attribute conversions, both on the Services and across other websites
        and applications you visit. This may include the use of cookies,
        pixels, tags, software development kits, mobile advertising
        identifiers, and similar tracking technologies operated by us or by our
        advertising partners.
      </p>
      <p>
        Advertising is how we keep guest and free-tier access to the Services
        free of charge. Where applicable law requires your consent before
        advertising cookies or similar technologies are used, we obtain that
        consent through the cookie-consent mechanism described in "Cookies and
        Consent Management" below, and you can withdraw it at any time as
        described there and in "Opting Out of Interest-Based Advertising."
      </p>

      <h3>4. Security, Fraud Prevention, and Legal Compliance</h3>
      <p>
        We use the information we collect to detect and prevent fraud, abuse,
        and security incidents; to enforce our terms of service; and to comply
        with applicable legal obligations, court orders, and lawful requests
        from governmental authorities.
      </p>

      <h2>Guest Access and Agreement by Use</h2>
      <p>
        Portions of the Services — such as guest chat with our AI agents — are
        available without creating an account. When you use the Services as a
        guest, we collect the prompts and messages you send, together with the
        device and connection metadata described above, and handle that
        information as described in this Privacy Policy. Guest sessions are
        temporary, are not linked to a registered account, and are not saved
        after they expire, although operational logs and the
        safety-and-abuse-prevention records described elsewhere in this Privacy
        Policy may be retained.
      </p>
      <p>
        Guest access is offered free of charge and is supported by advertising.
        When you use the Services as a guest, we may display advertisements —
        including interest-based advertisements served by third-party
        advertising providers — and we may share the device metadata,
        advertising identifiers, and usage information described in
        "Advertising and Marketing" above with those providers for that
        purpose, subject to the consent requirements of applicable law.
      </p>
      <p>
        <strong>
          By submitting a prompt or message to an agent as a guest, or by
          otherwise using the Services, you agree to our{' '}
          <Link to="/legal/terms">Terms of Service</Link>, this Privacy Policy,
          and our <Link to="/legal/ai-consent">AI Consent Notice</Link>.
        </strong>{' '}
        If you do not agree, do not use the Services.
      </p>

      <h2>Cookies and Consent Management</h2>
      <p>
        We use cookies, pixels, local storage, and similar technologies as
        described above. To present a cookie-consent banner, record your choices,
        and communicate those choices to our advertising and analytics partners,
        we use <strong>Termly</strong> as our Consent Management Platform
        ("CMP"). Termly implements the IAB Transparency &amp; Consent Framework
        (TCF) and the Global Privacy Platform (GPP) and, where applicable law
        requires it, prevents non-essential cookies from being set until you
        provide consent.
      </p>
      <p>
        You can review or withdraw your cookie and tracking consent at any time
        using the "Your Privacy Choices" control in our site footer, the Cookie
        Preferences link on your Account page, or our{' '}
        <Link to="/legal/cookie-policy">Cookie Policy</Link>. Withdrawing consent
        is as easy as granting it. For a description of the categories of cookies
        we use, see our{' '}
        <Link to="/legal/cookie-policy">Cookie Policy</Link>.
      </p>

      <h2>External Websites and Agent-Initiated Research</h2>
      <p>
        A central feature of the Services is the ability of our AI agents to
        gather information from external websites, search engines, knowledge
        bases, and third-party application programming interfaces ("APIs") on
        your behalf. When you ask an agent to research a topic, look something
        up, fetch a page, query a public dataset, or otherwise retrieve
        information from the public internet, portions of your inputs —
        including the text of your query, paraphrased or reformulated versions
        of your request, follow-up refinements, and any parameters needed to
        perform the lookup — will be transmitted to those external websites and
        services as an ordinary side-effect of performing the task you
        requested.
      </p>
      <p>
        This transmission is separate from, and in addition to, the
        advertising-related data sharing described above. It occurs because the
        external site or API must receive your query in order to return a
        useful result.
      </p>
      <p>
        You should assume that any external site, search engine, or API
        contacted on your behalf may log the request, may associate it with the
        IP address or other technical identifiers their systems receive, and
        may use the request in accordance with their own terms and privacy
        policies. We do not control how external sites or third-party APIs
        handle information they receive, and their handling is governed by
        their own policies, not by this Privacy Policy.
      </p>
      <p>
        If you do not want a particular piece of information transmitted to
        external services, do not include it in instructions given to an agent
        that performs web search, retrieval, or external-API access.
      </p>

      <h2>Voice Input and Speech Recognition</h2>
      <p>
        The agent console offers an optional voice-input feature ("Talk"). When
        you activate it, your browser's built-in speech-recognition capability
        captures audio from your microphone and converts it to text, which is
        placed in the message field for you to review and send. We do not
        receive, store, or process the raw audio of your voice; we receive only
        the transcribed text if and when you choose to send it, at which point it
        is treated like any other message you type.
      </p>
      <p>
        Depending on your browser, speech recognition may be performed by the
        browser vendor's own remote speech service rather than on your device. In
        particular, Google Chrome transmits captured audio to Google's
        speech-recognition servers for transcription. That processing is carried
        out by the browser vendor under its own terms and privacy policy, is
        outside our control, and is not governed by this Privacy Policy. The
        feature is entirely optional, is available only on supported browsers, and
        is never activated without your action — you may always type your messages
        instead.
      </p>

      <h2>Third-Party Service Providers</h2>
      <p>
        To operate the Services, we transmit portions of your data to the
        third-party service providers described below. Each provider receives
        only the data necessary to perform its function and is contractually
        obligated to protect your data with safeguards at least as protective
        as those described in this Privacy Policy.
      </p>
      <ul>
        <li>
          Large-language-model and generative-AI providers (for example,
          Anthropic Claude accessed through AWS Bedrock, and other model
          providers we may add over time), which receive your prompts,
          conversation context, and any attachments necessary to generate a
          response. When AI models are accessed through enterprise endpoints
          such as AWS Bedrock, the model provider does not use your inputs or
          outputs to train its models.
        </li>
        <li>
          Cloud-infrastructure providers (Amazon Web Services, Inc.), which
          host the Services and store your account data, conversation history,
          and uploaded content in AWS data centers located in the United
          States.
        </li>
        <li>
          Authentication providers (AWS Cognito), which receive your email
          address, password, and multi-factor authentication state in order to
          verify your identity and manage your account.
        </li>
        <li>
          Transactional email providers (AWS SES), which receive your email
          address in order to send verification messages, security
          notifications, and other account-related communications.
        </li>
        <li>
          Content-moderation, abuse-detection, and safety providers, which
          receive uploaded media and message text in order to detect unsafe
          content and other policy violations.
        </li>
        <li>
          Advertising, attribution, and analytics providers, as described in
          "How We Use Your Information" above.
        </li>
        <li>
          Consent-management providers (Termly), which operate our
          cookie-consent banner and preference center and process limited
          information — such as your consent choices, IP address, and device or
          browser identifiers — to record, signal, and honor your privacy
          preferences.
        </li>
      </ul>
      <p>
        By using the Services, you consent to the transmission of the data
        described above to the categories of service providers listed in this
        section.
      </p>

      <h2>Mobile Messaging and SMS</h2>
      <p>
        If you provide a mobile telephone number and opt in to receive text
        (SMS) messages — for example, to interact with one of our AI agents by
        text message or to receive account, verification, or service
        notifications — we use that number only to send the messages you have
        asked to receive and to provide related customer support. Message and
        data rates may apply, message frequency varies, and you can reply STOP
        at any time to cancel or HELP for help.
      </p>
      <p>
        <strong>
          No mobile information — including your mobile telephone number and
          your SMS opt-in or consent — will be sold, rented, or shared with any
          third parties or affiliates for their own marketing or promotional
          purposes.
        </strong>{' '}
        Your SMS opt-in and consent are not shared with any third parties, and
        are expressly excluded from every category of data collection, use, and
        sharing described elsewhere in this Privacy Policy, including the
        advertising, analytics, and third-party-service-provider disclosures
        above. We share your mobile number only with the telecommunications and
        messaging vendors that transmit the message (such as our messaging
        platform and the mobile carriers), and with service providers that
        perform support functions on our behalf under confidentiality
        obligations, in each case strictly as necessary to deliver the messages
        you requested. Text messaging originator opt-in data and consent are not
        shared with any third parties.
      </p>

      <h2>Cloud Infrastructure</h2>
      <p>
        The Services run on Amazon Web Services (AWS). Your data is stored and
        processed in AWS data centers located in the United States. If you
        access the Services from outside the United States, your information
        will be transferred to, stored in, and processed in the United States.
      </p>

      <h2>Data Protection</h2>
      <p>
        We make every reasonable effort to protect your personal information
        against unauthorized access, disclosure, alteration, and destruction.
        Our safeguards include:
      </p>
      <ul>
        <li>Encryption of data in transit using industry-standard TLS.</li>
        <li>Encryption of data at rest in our cloud-infrastructure provider's managed storage services.</li>
        <li>
          Access controls limiting personnel access to user data to the minimum
          necessary for operational support and incident response.
        </li>
        <li>Continuous monitoring of our systems for unauthorized access and unusual activity.</li>
        <li>Use of cloud-provider-managed lifecycle controls to enforce automatic expiration of transient data.</li>
      </ul>
      <p>
        No security measure is perfect, and no method of internet transmission
        or electronic storage is one-hundred-percent secure. We cannot
        guarantee absolute security, but we will continue to evolve our
        protections as the threat landscape changes.
      </p>

      <h2>Regulatory Compliance</h2>
      <p>
        We design and operate the Services in accordance with applicable
        data-privacy and consent regulations. Depending on where you reside,
        this may include the European Union General Data Protection Regulation
        (GDPR), the United Kingdom GDPR, the California Consumer Privacy Act
        as amended by the California Privacy Rights Act (CCPA / CPRA), the
        Virginia Consumer Data Protection Act, the Colorado Privacy Act, and
        other applicable state, national, and supranational privacy statutes.
        Where any such regulation grants you specific rights, we will honor
        those rights in accordance with the timelines and verification
        procedures required by the applicable law.
      </p>

      <h2>Data Retention</h2>
      <p>
        We retain personal information for as long as necessary to provide the
        Services to you, comply with our legal obligations, resolve disputes,
        and enforce our agreements. Conversation histories, prompts, uploaded
        files, and project state are retained while your account is active,
        and may be expired automatically after a period of inactivity in
        accordance with our retention schedules. Operational logs, abuse
        reports, and moderation records may be retained for longer periods for
        user-safety and regulatory-compliance purposes.
      </p>
      <p>
        When you delete your account, we remove your email address, account
        identifier, and active conversation history from our active systems.
        Backups and audit logs may continue to contain remnants of your data
        for a limited additional period until they are rotated out in the
        ordinary course.
      </p>

      <h2>Your Rights and Choices</h2>
      <p>
        Depending on your jurisdiction, you may have one or more of the
        following rights with respect to your personal information:
      </p>
      <ul>
        <li>The right to access the personal information we hold about you.</li>
        <li>The right to request correction of inaccurate personal information.</li>
        <li>The right to request deletion of your personal information.</li>
        <li>The right to object to, or restrict, certain processing of your personal information.</li>
        <li>The right to data portability.</li>
        <li>
          The right to opt out of the sale or sharing of personal information
          for cross-context behavioral advertising, where applicable.
        </li>
        <li>The right to withdraw consent at any time, where processing is based on consent.</li>
      </ul>
      <p>
        To exercise any of these rights, contact us using the information
        provided at the end of this Privacy Policy. We will respond to
        verifiable requests within the timeframes required by applicable law.
        We will not discriminate against you for exercising a privacy right.
      </p>
      <p>
        You may also delete your account directly from within the Services by
        opening Settings and selecting "Delete Account." Account deletion
        removes your email address, account identifier, and associated content
        from our active systems, subject to the limitations described in "Data
        Retention" above.
      </p>

      <h2>Opting Out of Interest-Based Advertising</h2>
      <p>
        You can manage your cookie and tracking consent directly through our{' '}
        <Link to="/legal/cookie-preferences">Cookie Preferences</Link> control.
        In addition, you may opt out of interest-based advertising delivered by
        participating advertising networks by visiting:
      </p>
      <ul>
        <li>
          Digital Advertising Alliance:{' '}
          <a href="https://optout.aboutads.info" target="_blank" rel="noreferrer">
            https://optout.aboutads.info
          </a>
        </li>
        <li>
          Network Advertising Initiative:{' '}
          <a href="https://optout.networkadvertising.org" target="_blank" rel="noreferrer">
            https://optout.networkadvertising.org
          </a>
        </li>
        <li>
          European Interactive Digital Advertising Alliance:{' '}
          <a href="https://www.youronlinechoices.eu" target="_blank" rel="noreferrer">
            https://www.youronlinechoices.eu
          </a>
        </li>
      </ul>
      <p>
        Opting out of interest-based advertising does not stop you from seeing
        ads; it only stops them from being personalized.
      </p>

      <h2>Do-Not-Track Signals</h2>
      <p>
        Some browsers transmit "Do Not Track" signals to websites. Because
        there is no industry consensus on how to interpret these signals, the
        Services do not currently respond differently when a Do Not Track
        signal is received. Where applicable law requires us to honor a
        particular opt-out preference signal (such as Global Privacy Control, or
        "GPC"), we do so: our consent management platform detects the GPC signal
        and applies the corresponding opt-out for that browser. You can also
        review or change your choices at any time through our{' '}
        <Link to="/legal/cookie-preferences">Cookie Preferences</Link> control.
      </p>

      <h2>Eligibility</h2>
      <p>
        The Services are available only to individuals who are eighteen (18)
        years of age or older. By creating an account or otherwise using the
        Services, you represent and warrant that you are at least eighteen
        years old. We do not knowingly collect or process information from
        individuals under the age of eighteen. If we become aware that we have
        collected personal information from a user under eighteen, we will
        delete that information promptly. Because the Services are intended
        exclusively for adults, this Privacy Policy does not address the
        collection of information from minors.
      </p>

      <h2>Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Any changes will
        be reflected by updating the "Last updated" date at the top of this
        document. Material changes will additionally be communicated through
        the Services or by email to the address associated with your account.
        We encourage you to review this Privacy Policy periodically to stay
        informed about how we protect your information.
      </p>
      <p>
        By continuing to use the Services after the effective date of an
        updated Privacy Policy, you acknowledge and accept the changes.
      </p>

      <h2>Contact Information</h2>
      <address>
        Andrew Paris LLC, d/b/a MediaShare-Link<br />
        5900 Balcones Dr Suite 21007<br />
        Austin, TX 78731<br />
        United States
      </address>
      <p>
        Email: <a href="mailto:Help@OculusMind.AI">Help@OculusMind.AI</a>
      </p>
    </LegalShell>
  );
}
